/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

// metrics

export enum METRICS {
  RISK = 'RISK',
  THREAT = 'THREAT',
  VULNERABILITY = 'VULNERABILITY',
  PROGRAM = 'PROGRAM',
  AUDIT = 'AUDIT',
  CONTROL = 'CONTROL',
  ASSESSMENT = 'ASSESSMENT',
  REQUEST = 'REQUEST',
  VENDOR = 'VENDOR',
  FACILITY = 'FACILITY',
  INCIDENT = 'INCIDENT',
  TASK = 'TASK',
  ISSUE = 'ISSUE',
  MARKET = 'MARKET',
  ORGANIZATION_GROUP = 'ORGANIZATION_GROUP',
  SURVEY_RESPONSE = 'SURVEY_RESPONSE',
  SYSTEM = 'SYSTEM',
  PROCESS = 'PROCESS',
  SECTION = 'SECTION',
  STANDARD = 'STANDARD',
  OBJECTIVE = 'OBJECTIVE',
  CLAUSE = 'CLAUSE',
  CONTRACT = 'CONTRACT',
  DATA_ASSET = 'DATA_ASSET',
  DEVIATION = 'DEVIATION',
  POLICY = 'POLICY',
  PRODUCT = 'PRODUCT',
  PROJECT = 'PROJECT',
}

// dimensions

export enum DIMENSIONS {
  STATUS = 'STATUS',
  TITLE = 'TITLE',
  OWNER_NAME = 'OWNER_NAME',
  CREATED_AT = 'CREATED_AT',
  END_DATE = 'END_DATE',
  ASSIGNEE = 'ASSIGNEE',
  DESIGN = 'DESIGN',
  OPERATIONALLY = 'OPERATIONALLY',
  CONCLUSION = 'CONCLUSION',
  RISK_RATING = 'RISK_RATING',
  RATING = 'RATING',
  RESPONDED_AT = 'RESPONDED_AT',
  SURVEY_TAKER_NAME = 'SURVEY_TAKER_NAME',
  ASSOCIATED_OBJECT = 'ASSOCIATED_OBJECT',
  AUDIT_MANAGERS = 'AUDIT_MANAGERS',
  RELATED_REQUESTERS = 'RELATED_REQUESTERS',
  RELATED_VERIFIERS = 'RELATED_VERIFIERS',
  RELATED_REVIEWERS = 'RELATED_REVIEWERS',
  RELATED_ASSESSORS = 'RELATED_ASSESSORS',
  SENT_AT = 'SENT_AT',
  RESENT_AT = 'RESENT_AT',
  RESENT_COUNTER = 'RESENT_COUNTER',
  PROGRAM_OWNERS = 'PROGRAM_OWNERS',
  POLICY_KIND = 'KIND',
  PRODUCT_KIND = 'PRODUCT_KIND',
  TAG = 'TAG',
}

// visualizations

export enum VISUALIZATIONS {
  PIE_CHART = 'PIE_CHART',
  VERTICAL_BAR_CHART = 'VERTICAL_BAR_CHART',
  HORIZONTAL_BAR_CHART = 'HORIZONTAL_BAR_CHART',
  RICH_TEXT = 'RICH_TEXT',
}

export const VisualizationOptions = {
  [VISUALIZATIONS.PIE_CHART]: {
    label: 'Pie Chart',
  },
  [VISUALIZATIONS.VERTICAL_BAR_CHART]: {
    label: 'Column Chart',
  },
  [VISUALIZATIONS.HORIZONTAL_BAR_CHART]: {
    label: 'Bar Chart',
  },
  [VISUALIZATIONS.RICH_TEXT]: {
    label: 'Rich Text',
  },
};

interface MetricOption {
  label: string;
  model: string;
  plural: string;
  dimensions: DIMENSIONS[];
}

export const MetricOptions: { [key in METRICS]: MetricOption } = {
  [METRICS.RISK]: {
    label: 'Risk',
    model: 'Risk',
    plural: 'Risks',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.THREAT]: {
    label: 'Threat',
    model: 'Threat',
    plural: 'Threats',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.VULNERABILITY]: {
    label: 'Vulnerability',
    model: 'Vulnerability',
    plural: 'Vulnerabilities',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.PROGRAM]: {
    label: 'Program',
    model: 'Program',
    plural: 'Programs',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.PROGRAM_OWNERS, DIMENSIONS.TAG],
  },
  [METRICS.AUDIT]: {
    label: 'Audit',
    model: 'Audit',
    plural: 'Audits',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.STATUS, DIMENSIONS.AUDIT_MANAGERS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.CONTROL]: {
    label: 'Control',
    model: 'Control',
    plural: 'Controls',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.ASSESSMENT]: {
    label: 'Assessment',
    model: 'Assessment',
    plural: 'Assessments',
    dimensions: [
      DIMENSIONS.TITLE,
      DIMENSIONS.OWNER_NAME,
      DIMENSIONS.STATUS,
      DIMENSIONS.CREATED_AT,
      DIMENSIONS.END_DATE,
      DIMENSIONS.DESIGN,
      DIMENSIONS.OPERATIONALLY,
      DIMENSIONS.CONCLUSION,
      DIMENSIONS.RELATED_VERIFIERS,
      DIMENSIONS.RELATED_ASSESSORS,
      DIMENSIONS.TAG,
    ],
  },
  [METRICS.REQUEST]: {
    label: 'Request',
    model: 'Request',
    plural: 'Requests',
    dimensions: [
      DIMENSIONS.TITLE,
      DIMENSIONS.RELATED_REQUESTERS,
      DIMENSIONS.STATUS,
      DIMENSIONS.ASSIGNEE,
      DIMENSIONS.CREATED_AT,
      DIMENSIONS.END_DATE,
      DIMENSIONS.RELATED_VERIFIERS,
      DIMENSIONS.RELATED_REVIEWERS,
      DIMENSIONS.TAG,
    ],
  },
  [METRICS.VENDOR]: {
    label: 'Vendor',
    model: 'Vendor',
    plural: 'Vendors',
    dimensions: [
      DIMENSIONS.TITLE,
      DIMENSIONS.OWNER_NAME,
      DIMENSIONS.STATUS,
      DIMENSIONS.CREATED_AT,
      DIMENSIONS.RISK_RATING,
      DIMENSIONS.TAG,
    ],
  },
  [METRICS.FACILITY]: {
    label: 'Facility',
    model: 'Facility',
    plural: 'Facilities',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.INCIDENT]: {
    label: 'Incident',
    model: 'Incident',
    plural: 'Incidents',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.TASK]: {
    label: 'Task',
    model: 'Task',
    plural: 'Tasks',
    dimensions: [
      DIMENSIONS.TITLE,
      DIMENSIONS.STATUS,
      DIMENSIONS.ASSIGNEE,
      DIMENSIONS.CREATED_AT,
      DIMENSIONS.END_DATE,
      DIMENSIONS.RELATED_REVIEWERS,
      DIMENSIONS.RELATED_VERIFIERS,
      DIMENSIONS.TAG,
    ],
  },
  [METRICS.ISSUE]: {
    label: 'Issue',
    model: 'Issue',
    plural: 'Issues',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.MARKET]: {
    label: 'Market',
    model: 'Market',
    plural: 'Markets',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.ORGANIZATION_GROUP]: {
    label: 'Organization Group',
    model: 'OrgGroup',
    plural: 'Organization Groups',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.SURVEY_RESPONSE]: {
    label: 'Questionnaire Responses',
    model: 'SurveyResponse',
    plural: 'Questionnaire Responses',
    dimensions: [
      DIMENSIONS.TITLE,
      DIMENSIONS.STATUS,
      DIMENSIONS.CREATED_AT,
      DIMENSIONS.RESPONDED_AT,
      DIMENSIONS.SURVEY_TAKER_NAME,
      DIMENSIONS.RATING,
      DIMENSIONS.ASSOCIATED_OBJECT,
      DIMENSIONS.SENT_AT,
      DIMENSIONS.RESENT_AT,
      DIMENSIONS.RESENT_COUNTER,
      DIMENSIONS.TAG,
    ],
  },
  [METRICS.SYSTEM]: {
    label: 'System',
    model: 'System',
    plural: 'Systems',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.OWNER_NAME, DIMENSIONS.TAG],
  },
  [METRICS.OBJECTIVE]: {
    label: 'Objective',
    model: 'Objective',
    plural: 'Objectives',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.OWNER_NAME, DIMENSIONS.TAG],
  },
  [METRICS.PROCESS]: {
    label: 'Process',
    model: 'Process',
    plural: 'Processes',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.OWNER_NAME, DIMENSIONS.TAG],
  },
  [METRICS.SECTION]: {
    label: 'Section',
    model: 'Section',
    plural: 'Sections',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.OWNER_NAME, DIMENSIONS.TAG],
  },
  [METRICS.STANDARD]: {
    label: 'Standard',
    model: 'Standard',
    plural: 'Standards',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.OWNER_NAME, DIMENSIONS.TAG],
  },
  [METRICS.CLAUSE]: {
    label: 'Clause',
    model: 'Clause',
    plural: 'Clauses',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.CONTRACT]: {
    label: 'Contract',
    model: 'Contract',
    plural: 'Contracts',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.DATA_ASSET]: {
    label: 'Data Asset',
    model: 'DataAsset',
    plural: 'Data Assets',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.DEVIATION]: {
    label: 'Exception',
    model: 'Deviation',
    plural: 'Exceptions',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
  [METRICS.POLICY]: {
    label: 'Policy',
    model: 'Policy',
    plural: 'Policies',
    dimensions: [
      DIMENSIONS.TITLE,
      DIMENSIONS.OWNER_NAME,
      DIMENSIONS.STATUS,
      DIMENSIONS.CREATED_AT,
      DIMENSIONS.POLICY_KIND,
      DIMENSIONS.TAG,
    ],
  },
  [METRICS.PRODUCT]: {
    label: 'Product',
    model: 'Product',
    plural: 'Products',
    dimensions: [
      DIMENSIONS.TITLE,
      DIMENSIONS.OWNER_NAME,
      DIMENSIONS.STATUS,
      DIMENSIONS.CREATED_AT,
      DIMENSIONS.PRODUCT_KIND,
      DIMENSIONS.TAG,
    ],
  },
  [METRICS.PROJECT]: {
    label: 'Project',
    model: 'Project',
    plural: 'Projects',
    dimensions: [DIMENSIONS.TITLE, DIMENSIONS.OWNER_NAME, DIMENSIONS.STATUS, DIMENSIONS.CREATED_AT, DIMENSIONS.TAG],
  },
};

export enum DIMENSION_TYPES {
  DATE = 'DATE',
  RELEVANT = 'RELEVANT',
}

interface DimensionOption {
  label: string;
  attribute: string;
  type?: DIMENSION_TYPES;
  drilldownDisabled?: boolean;
  emptyValue?: string;
  relevantType?: string;
  labelMappings?: Object;
}

export const DimensionOptions: { [key in DIMENSIONS]: DimensionOption } = {
  [DIMENSIONS.TITLE]: {
    label: 'Title',
    attribute: 'title',
  },
  [DIMENSIONS.STATUS]: {
    label: 'Status',
    attribute: 'status',
    labelMappings: {},
  },
  [DIMENSIONS.OWNER_NAME]: {
    label: 'Owner Name',
    attribute: 'owners',
  },
  [DIMENSIONS.CREATED_AT]: {
    label: 'Date Created',
    attribute: 'created_at',
    type: DIMENSION_TYPES.DATE,
  },
  [DIMENSIONS.END_DATE]: {
    label: 'Due On',
    attribute: 'end_date',
    type: DIMENSION_TYPES.DATE,
  },
  [DIMENSIONS.ASSIGNEE]: {
    label: 'Assignee',
    attribute: 'assignee',
  },
  [DIMENSIONS.DESIGN]: {
    label: 'Design Effectiveness',
    attribute: 'design',
  },
  [DIMENSIONS.OPERATIONALLY]: {
    label: 'Operation Effectiveness',
    attribute: 'operationally',
  },
  [DIMENSIONS.CONCLUSION]: {
    label: 'Overall Effectiveness',
    attribute: 'conclusion',
  },
  [DIMENSIONS.RISK_RATING]: {
    label: 'Risk Rating',
    attribute: 'risk_rating',
  },
  [DIMENSIONS.RATING]: {
    label: 'Risk Rating',
    attribute: 'rating',
    emptyValue: 'not applicable',
  },
  [DIMENSIONS.RESPONDED_AT]: {
    label: 'Responded At',
    attribute: 'responded_at',
    type: DIMENSION_TYPES.DATE,
  },
  [DIMENSIONS.SURVEY_TAKER_NAME]: {
    label: 'Respondant',
    attribute: 'survey_taker_name',
  },
  [DIMENSIONS.ASSOCIATED_OBJECT]: {
    label: 'Associated Object',
    attribute: 'subject',
    drilldownDisabled: true,
  },
  [DIMENSIONS.RELATED_REQUESTERS]: {
    label: 'Requester',
    attribute: 'related_requesters',
  },
  [DIMENSIONS.AUDIT_MANAGERS]: {
    label: 'Audit Manager',
    attribute: 'audit_managers',
    type: DIMENSION_TYPES.RELEVANT,
    relevantType: 'Person',
  },
  [DIMENSIONS.RELATED_VERIFIERS]: {
    label: 'Verifier',
    attribute: 'related_verifiers',
  },
  [DIMENSIONS.RELATED_REVIEWERS]: {
    label: 'Reviewer',
    attribute: 'reviewers',
  },
  [DIMENSIONS.RELATED_ASSESSORS]: {
    label: 'Assessor',
    attribute: 'related_assessors',
  },
  [DIMENSIONS.SENT_AT]: {
    label: 'First sent on',
    attribute: 'sent_at',
    type: DIMENSION_TYPES.DATE,
  },
  [DIMENSIONS.RESENT_AT]: {
    label: 'Last re-send date',
    attribute: 'resent_at',
    type: DIMENSION_TYPES.DATE,
  },
  [DIMENSIONS.RESENT_COUNTER]: {
    label: 'Re-sent count',
    attribute: 'resent_counter',
  },
  [DIMENSIONS.PROGRAM_OWNERS]: {
    label: 'Manager',
    attribute: 'program_owners',
    type: DIMENSION_TYPES.RELEVANT,
    relevantType: 'Person',
  },
  [DIMENSIONS.POLICY_KIND]: {
    label: 'Kind/Type',
    attribute: 'kind',
  },
  [DIMENSIONS.PRODUCT_KIND]: {
    label: 'Kind/Type',
    attribute: 'kind',
  },
  [DIMENSIONS.TAG]: {
    label: 'Tags',
    attribute: 'tags',
  },
};
